import API_ENDPOINTS from "../components/config/apiConfig";
import sha1 from "sha1";
import axios from "axios";
import { getFirebaseToken } from "../utils/auth";

// Helper to get Firebase auth token
async function getAuthToken() {
  return await getFirebaseToken();
}

const MEMBERSHIP_STATUS_CACHE_PREFIX = "membershipStatus:";

export function getMembershipStatusCacheKey(spId) {
  return `${MEMBERSHIP_STATUS_CACHE_PREFIX}${spId}`;
}

export function getCachedMembershipStatus(spId) {
  if (!spId) return null;

  try {
    const cached = localStorage.getItem(getMembershipStatusCacheKey(spId));
    return cached ? JSON.parse(cached) : null;
  } catch (error) {
    console.error("Error reading cached membership status:", error);
    return null;
  }
}

export function cacheMembershipStatus(spId, membershipStatus) {
  if (!spId || !membershipStatus) return;

  try {
    localStorage.setItem(
      getMembershipStatusCacheKey(spId),
      JSON.stringify({
        ...membershipStatus,
        cachedAt: new Date().toISOString(),
      })
    );
  } catch (error) {
    console.error("Error caching membership status:", error);
  }
}

// Convert file to base64 (remove data:image/...;base64, prefix)
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      const base64 = result.replace(/^data:image\/[a-zA-Z0-9+]+;base64,/, "");
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Upload image to API
export async function uploadImageApi(userId, base64) {
  try {
    const token = await getAuthToken();
    console.log('Uploading image - Token exists:', !!token, 'UserId:', userId, 'Base64 length:', base64?.length);
    
    if (!token) {
      throw new Error('Unable to authenticate. Please sign in and try again.');
    }
    
    const response = await fetch(API_ENDPOINTS.UPLOAD_BASE64_IMAGE, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      },
      body: JSON.stringify({
        userId,
        base64Image: base64
      })
    });
    
    console.log('Upload response status:', response.status, 'OK:', response.ok);
    
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unable to read error response');
      console.error('Upload failed with status:', response.status, 'Error:', errorText);
      throw new Error(`Upload failed with status ${response.status}: ${errorText}`);
    }
    
    const data = await response.json();
    console.log('Upload successful, response:', data);
    return data;
  } catch (error) {
    console.error('Error uploading image:', error);
    console.error('Error details:', {
      message: error.message,
      name: error.name,
      stack: error.stack
    });
    throw error;
  }
}

// Validate if mobile number exists in database
export async function validateMobile(mobile) {
  try {
    const response = await fetch(API_ENDPOINTS.VALIDATE_MOBILE(mobile), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'accept': '*/*'
      }
    });
    
    if (response.ok) {
      const data = await response.json();
      return data;
    }
    return { responseCode: 500, responseMessage: 'Failed to validate mobile', responseData: [false] };
  } catch (error) {
    console.error('Error validating mobile:', error);
    return { responseCode: 500, responseMessage: 'Error validating mobile', responseData: [false] };
  }
}

// Get membership status for a service provider
export async function getMembershipStatus(spId) {
  try {
    const token = await getAuthToken();
    const response = await fetch(API_ENDPOINTS.GET_MEMBERSHIP_STATUS(spId), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'accept': '*/*',
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (response.ok) {
      const data = await response.json();
      cacheMembershipStatus(spId, data);
      return data;
    }
    return { responseCode: 500, responseMessage: 'Failed to get membership status', responseData: null, hasMembership: false };
  } catch (error) {
    console.error('Error getting membership status:', error);
    return { responseCode: 500, responseMessage: 'Error getting membership status', responseData: null, hasMembership: false };
  }
}

export async function updateServiceProviderLanguage(spId, language) {
  try {
    if (!spId || !language) {
      return {
        responseCode: 400,
        responseMessage: 'Invalid service provider id or language',
        responseData: null,
      };
    }

    const token = await getAuthToken();
    const response = await fetch(API_ENDPOINTS.UPDATE_SERVICE_PROVIDER_LANGUAGE(spId, language), {
      method: 'PUT',
      headers: {
        accept: '*/*',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    const data = await response.json().catch(() => null);

    if (response.ok && data) {
      return data;
    }

    return (
      data || {
        responseCode: response.status || 500,
        responseMessage: 'Failed to update language',
        responseData: null,
      }
    );
  } catch (error) {
    console.error('Error updating service provider language:', error);
    return {
      responseCode: 500,
      responseMessage: 'Error updating language',
      responseData: null,
    };
  }
}

// Get average rating for a service provider
export async function getServiceProviderAverageRating(spId) {
  try {
    if (!spId) {
      return { responseCode: 400, responseMessage: "Invalid service provider id", responseData: 0 };
    }

    const token = await getAuthToken();
    const response = await fetch(API_ENDPOINTS.GET_PROVIDER_AVERAGE_RATING(spId), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        accept: "*/*",
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.ok) {
      const data = await response.json();
      return data;
    }

    return {
      responseCode: response.status || 500,
      responseMessage: "Failed to get provider average rating",
      responseData: 0,
    };
  } catch (error) {
    console.error("Error getting provider average rating:", error);
    return {
      responseCode: 500,
      responseMessage: "Error getting provider average rating",
      responseData: 0,
    };
  }
}

export async function registerFCMToken(userId, token) {
  try {
    const authToken = await getAuthToken();
    console.log('registerFCMToken - Firebase token exists:', !!authToken, 'Token length:', authToken?.length);
    
    const headers = {
      'Content-Type': 'application/json'
    };
    
    // Only add Authorization header if we have a Firebase token
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
      console.log('registerFCMToken - Authorization header added');
    } else {
      console.warn('registerFCMToken - No Firebase token available, request may fail');
    }
    
    const response = await fetch(API_ENDPOINTS.REGISTER_FCM_TOKEN, {
      method: 'POST',
      headers,
      body: JSON.stringify({ userId, fcmToken: token, deviceType: 'WEB', userType: 'SP' })
    });
    
    console.log('registerFCMToken - Response status:', response.status, 'OK:', response.ok);
    
    if (response.ok) {
      localStorage.setItem('fcmToken', token);
      localStorage.setItem('fcmTokenRegistered', 'true');
      return true;
    }
    return false;
  } catch (error) {
    console.error('registerFCMToken - Error:', error);
    return false;
  }
}

export async function updateFCMToken(userId, token) {
  try {
    const authToken = await getAuthToken();
    
    const headers = {
      'Content-Type': 'application/json'
    };
    
    // Only add Authorization header if we have a Firebase token
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    
    const response = await fetch(API_ENDPOINTS.UPDATE_FCM_TOKEN, {
      method: 'POST',
      headers,
      body: JSON.stringify({ userId, fcmToken: token, deviceType: 'WEB' })
    });
    if (response.ok) {
      localStorage.setItem('fcmToken', token);
      localStorage.setItem('fcmTokenRegistered', 'true');
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

// Add vehicle API
export async function addVehicleApi(form, userId) {
  try {
    const token = await getAuthToken();
    const response = await fetch(API_ENDPOINTS.ADD_VEHICLE, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      },
      body: JSON.stringify({ ...form, userId }),
    });
    return response.json();
  } catch (error) {
    console.error('Error adding vehicle:', error);
    return { success: false, error: error.message };
  }
}

// Fetch vehicle details by ID
export async function fetchVehicleById(cabId) {
  try {
    const token = await getAuthToken();
    const res = await fetch(API_ENDPOINTS.GET_VEHICLE_BY_ID(cabId), {
      headers: {
        "Authorization": `Bearer ${token}`
      }
    });
    const data = await res.json();
    if (res.ok && data.responseCode === 200 && data.responseData) {
      return data.responseData;
    }
    return null;
  } catch (error) {
    console.error('Error fetching vehicle by ID:', error);
    return null;
  }
}

// Get vehicle suggestions
export async function getVehicleSuggestions(query) {
  try {
    const token = await getAuthToken();
    const res = await fetch(API_ENDPOINTS.VEHICLE_SUGGESTIONS + encodeURIComponent(query), {
      headers: {
        "Authorization": `Bearer ${token}`
      }
    });
    return res.json();
  } catch (error) {
    console.error('Error getting vehicle suggestions:', error);
    return [];
  }
}

// Update vehicle by registration ID
export async function updateVehicleByCabId(payload) {
  try {
    const token = await getAuthToken();
    const res = await fetch(
      API_ENDPOINTS.UPDATE_VEHICLE_BY_REG_ID(payload.cabId),
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(payload),
      }
    );
    return res.json();
  } catch (error) {
    console.error('Error updating vehicle:', error);
    return { success: false, error: error.message };
  }
}

export async function sendForgotPasswordOtp(email) {
  try {
    const token = await getAuthToken();
    const response = await fetch(API_ENDPOINTS.FORGOT_PASSWORD, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      },
      body: email,
    });
    return response.json();
  } catch (error) {
    console.error('Error sending forgot password OTP:', error);
    return { success: false, error: error.message };
  }
}

export async function resetPasswordApi({ otp, newPassword, username }) {
  try {
    const token = await getAuthToken();
    const response = await fetch(API_ENDPOINTS.RESET_PASSWORD, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      },
      body: JSON.stringify({
        otp,
        newPassword: sha1(newPassword),
        username,
      }),
    });
    return response.json();
  } catch (error) {
    console.error('Error resetting password:', error);
    return { success: false, error: error.message };
  }
}

export async function uploadProfileImageApi(payload) {
  try {
    // payload: { userId, base64Image }
    const token = await getAuthToken();
    return axios.post(API_ENDPOINTS.UPLOAD_BASE64_IMAGE, payload, {
      headers: {
        "Authorization": `Bearer ${token}`
      }
    });
  } catch (error) {
    console.error('Error uploading profile image:', error);
    throw error;
  }
}

export async function updateUserProfileApi(updatePayload) {
  try {
    // PATCH user profile
    const token = await getAuthToken();
    return axios.patch(API_ENDPOINTS.UPDATE_USER, updatePayload, {
      headers: {
        "Authorization": `Bearer ${token}`
      }
    });
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
}

export async function fetchBookingsBySPId(userId) {
  try {
    const token = await getAuthToken();
    const res = await axios.get(API_ENDPOINTS.GET_ALL_BOOKINGS_BY_VENDOR_ID(userId), {
      headers: {
        "Authorization": `Bearer ${token}`
      }
    });
    return res.data || [];
  } catch (error) {
    console.error('Error fetching bookings by SP ID:', error);
    return [];
  }
}

export async function deleteUserAccount(userId) {
  try {
    const token = await getAuthToken();
    return axios.delete(API_ENDPOINTS.DELETE_USER(userId), {
      headers: {
        "Authorization": `Bearer ${token}`
      }
    });
  } catch (error) {
    console.error('Error deleting user account:', error);
    throw error;
  }
}

// Deprecated: JWT token is no longer used. Firebase authentication is now used.
// This function is kept for backward compatibility but will not be called.
export async function getJwtToken() {
  console.warn('getJwtToken() is deprecated. Use Firebase authentication instead.');
  return null;
}

export async function registerUserApi(form, controller) {
  try {
    const token = await getAuthToken();
    
    const headers = {
      'Content-Type': 'application/json'
    };
    
    // Only add Authorization header if we have a Firebase token
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(API_ENDPOINTS.ADD_USER, {
      method: 'POST',
      headers,
      body: JSON.stringify(form),
      signal: controller ? controller.signal : undefined
    });
    return response.json();
  } catch (error) {
    console.error('Error registering user:', error);
    return { success: false, error: error.message };
  }
}

export async function activateFreeMembership(spId) {
  try {
    const token = await getAuthToken();
    
    const headers = {
      'Content-Type': 'application/json'
    };
    
    // Only add Authorization header if we have a Firebase token
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(API_ENDPOINTS.ACTIVATE_FREE_MEMBERSHIP(spId), {
      method: 'POST',
      headers,
      body: ''
    });
    
    if (response.ok) {
      console.log('Free membership activated successfully for:', spId);
      return { success: true, message: 'Free membership activated' };
    } else {
      console.error('Failed to activate membership, status:', response.status);
      return { success: false, message: 'Failed to activate membership' };
    }
  } catch (error) {
    console.error('Error activating free membership:', error);
    return { success: false, error: error.message };
  }
}

export async function getUserByMobile(phone) {
  try {
    const token = await getAuthToken();
    const response = await fetch(API_ENDPOINTS.GET_USER_BY_MOBILE(phone), {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.json();
  } catch (error) {
    console.error('Error getting user by mobile:', error);
    return { success: false, error: error.message };
  }
}

export async function deactivateServiceProvider(serviceProviderId) {
  try {
    const token = await getAuthToken();
    const response = await fetch(API_ENDPOINTS.DEACTIVATE_SERVICE_PROVIDER(serviceProviderId), {
      method: 'PUT',
      headers: {
        'accept': '*/*',
        'Authorization': `Bearer ${token}`
      }
    });
    return response.json();
  } catch (error) {
    console.error('Error deactivating service provider:', error);
    return { success: false, error: error.message };
  }
}

export async function updateNotificationSettings(serviceProviderId, mobile, email) {
  try {
    const token = await getAuthToken();
    const response = await fetch(API_ENDPOINTS.UPDATE_NOTIFICATION_SETTINGS(serviceProviderId, mobile, email), {
      method: 'PATCH',
      headers: {
        'accept': '*/*',
        'Authorization': `Bearer ${token}`
      }
    });
    return response.json();
  } catch (error) {
    console.error('Error updating notification settings:', error);
    return { success: false, error: error.message };
  }
}